import { Card } from "@/components/ui/card";
import { Lightbulb, ShoppingBag, Wand2, Package, Zap } from "lucide-react";
import { useEffect, useState } from "react";

export function HeroAnimatedFlow() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 4);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="relative w-full max-w-[240px] sm:max-w-[340px] md:max-w-[440px] lg:max-w-[520px] mx-auto"
      style={{ aspectRatio: "1/1" }}
    >
      {/* Center hub - AI Engine */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-2xl shadow-primary/50 animate-pulse-glow z-30">
        <div className="text-center">
          <Zap className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-primary-foreground mx-auto mb-0.5" />
          <span className="text-[8px] sm:text-[10px] font-bold text-primary-foreground">.ai</span>
        </div>
      </div>

      {/* Step 1: Your Idea (Top) */}
      <Card
        className={`absolute top-0 left-1/2 -translate-x-1/2 w-20 sm:w-28 md:w-36 lg:w-40 p-1.5 sm:p-2 md:p-3 lg:p-4 transition-all duration-500 z-30 bg-background ${
          activeStep === 0
            ? "scale-110 shadow-xl border-accent ring-2 ring-accent/50"
            : "scale-100 shadow-sm"
        }`}
      >
        <div className="flex flex-col items-center gap-0.5 sm:gap-1 md:gap-1.5">
          <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Lightbulb className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-blue-500" />
          </div>
          <span className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm font-bold">
            💡 Dream It Up
          </span>
          <span className="text-[6px] sm:text-[8px] md:text-[10px] lg:text-xs text-muted-foreground text-center leading-tight">
            What&apos;s your vibe?
          </span>
        </div>
      </Card>

      {/* Step 2: Choose Product (Right) */}
      <Card
        className={`absolute top-1/2 -right-8 sm:-right-10 md:-right-12 lg:-right-14 -translate-y-1/2 w-20 sm:w-28 md:w-36 lg:w-40 p-1.5 sm:p-2 md:p-3 lg:p-4 transition-all duration-500 z-30 bg-background ${
          activeStep === 1
            ? "scale-110 shadow-xl border-accent ring-2 ring-accent/50"
            : "scale-100 shadow-sm"
        }`}
      >
        <div className="flex flex-col items-center gap-0.5 sm:gap-1 md:gap-1.5">
          <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
            <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-orange-500" />
          </div>
          <span className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm font-bold">
            🛍️ Pick Product
          </span>
          <span className="text-[6px] sm:text-[8px] md:text-[10px] lg:text-xs text-muted-foreground text-center leading-tight">
            Mug? Shirt? Sticker?
          </span>
        </div>
      </Card>

      {/* Step 3: AI Sandbox (Bottom) */}
      <Card
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-20 sm:w-28 md:w-36 lg:w-40 p-1.5 sm:p-2 md:p-3 lg:p-4 transition-all duration-500 z-30 bg-background ${
          activeStep === 2
            ? "scale-110 shadow-xl border-accent ring-2 ring-accent/50"
            : "scale-100 shadow-sm"
        }`}
      >
        <div className="flex flex-col items-center gap-0.5 sm:gap-1 md:gap-1.5">
          <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Wand2 className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-purple-500" />
          </div>
          <span className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm font-bold">
            ✨ AI Magic
          </span>
          <span className="text-[6px] sm:text-[8px] md:text-[10px] lg:text-xs text-muted-foreground text-center leading-tight">
            Watch it come alive!
          </span>
        </div>
      </Card>

      {/* Step 4: Delivery (Left) */}
      <Card
        className={`absolute top-1/2 -left-8 sm:-left-10 md:-left-12 lg:-left-14 -translate-y-1/2 w-20 sm:w-28 md:w-36 lg:w-40 p-1.5 sm:p-2 md:p-3 lg:p-4 transition-all duration-500 z-30 bg-background ${
          activeStep === 3
            ? "scale-110 shadow-xl border-accent ring-2 ring-accent/50"
            : "scale-100 shadow-sm"
        }`}
      >
        <div className="flex flex-col items-center gap-0.5 sm:gap-1 md:gap-1.5">
          <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
            <Package className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-green-500" />
          </div>
          <span className="text-[8px] sm:text-[10px] md:text-xs lg:text-sm font-bold">
            📦 Pure Joy
          </span>
          <span className="text-[6px] sm:text-[8px] md:text-[10px] lg:text-xs text-muted-foreground text-center leading-tight">
            It&apos;s on its way!
          </span>
        </div>
      </Card>

      {/* Animated connection lines */}
      <svg className="absolute inset-0 w-full h-full z-20" style={{ overflow: "visible" }}>
        {/* Line from Idea to Center */}
        <line
          x1="50%"
          y1="20%"
          x2="50%"
          y2="50%"
          stroke="currentColor"
          strokeWidth="2"
          className={`text-blue-500 transition-all duration-500 ${
            activeStep === 0 ? "opacity-100" : "opacity-20"
          }`}
          strokeDasharray="5,5"
        >
          {activeStep === 0 && (
            <animate
              attributeName="stroke-dashoffset"
              from="10"
              to="0"
              dur="1s"
              repeatCount="indefinite"
            />
          )}
        </line>

        {/* Line from Center to Choose Product */}
        <line
          x1="92%"
          y1="50%"
          x2="50%"
          y2="50%"
          stroke="currentColor"
          strokeWidth="2"
          className={`text-orange-500 transition-all duration-500 ${
            activeStep === 1 ? "opacity-100" : "opacity-20"
          }`}
          strokeDasharray="5,5"
        >
          {activeStep === 1 && (
            <animate
              attributeName="stroke-dashoffset"
              from="10"
              to="0"
              dur="1s"
              repeatCount="indefinite"
            />
          )}
        </line>

        {/* Line from Center to AI Sandbox */}
        <line
          x1="50%"
          y1="80%"
          x2="50%"
          y2="50%"
          stroke="currentColor"
          strokeWidth="2"
          className={`text-purple-500 transition-all duration-500 ${
            activeStep === 2 ? "opacity-100" : "opacity-20"
          }`}
          strokeDasharray="5,5"
        >
          {activeStep === 2 && (
            <animate
              attributeName="stroke-dashoffset"
              from="10"
              to="0"
              dur="1s"
              repeatCount="indefinite"
            />
          )}
        </line>

        {/* Line from Center to Delivery */}
        <line
          x1="8%"
          y1="50%"
          x2="50%"
          y2="50%"
          stroke="currentColor"
          strokeWidth="2"
          className={`text-green-500 transition-all duration-500 ${
            activeStep === 3 ? "opacity-100" : "opacity-20"
          }`}
          strokeDasharray="5,5"
        >
          {activeStep === 3 && (
            <animate
              attributeName="stroke-dashoffset"
              from="10"
              to="0"
              dur="1s"
              repeatCount="indefinite"
            />
          )}
        </line>
      </svg>
    </div>
  );
}
